/**
 * ImageEditorModal — WhatsApp-style image viewer + editor.
 *
 * View Mode (always initial):
 *   • X top-left  → closes modal entirely (onCancel)
 *   • Pencil / T icons top-right → enter Edit Mode
 *   • Save button → uploads all local edits (onSave)
 *   • Arrows + filmstrip → navigate between images
 *
 * Edit Mode (per image):
 *   • Canvas created ONCE per image — persists while editing
 *   • Pencil + T are tool selectors; switching does NOT reset canvas
 *   • Can draw AND write text freely; all changes accumulate
 *   • X top-left  → exit without saving, back to View Mode
 *   • Undo → reverts last added object
 *   • Clear (trash) → removes all annotations
 *   • Done → saves canvas locally, back to View Mode
 *   • Navigation arrows and filmstrip hidden in Edit Mode
 *
 * Renders into document.body via ReactDOM.createPortal (z-index 99999).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import FocusLock from 'react-focus-lock'
import { Flex, PseudoBox, Text, Button } from '@chakra-ui/core'
import { useCameraCapture } from 'hooks/useCameraCapture'
import {
  Pencil,
  RotateCcw,
  Trash2,
  Type,
  X,
  ChevronLeft,
  ChevronRight,
  Circle,
  Square,
  ArrowRight,
  Camera,
  ImagePlus,
} from 'lucide-react'
import useLocaleText from 'components/useLocaleText'

export interface ImageEditorModalProps {
  images: string[]
  /** Keep a nested result annotation above its parent viewer in the focus stack. */
  trapFocus?: boolean
  startIndex?: number
  isOpen: boolean
  onSave: (edits: Record<number, Blob>) => void
  onCancel: () => void
  hasLocalImages?: boolean
  /** When set to 'comment', shows a source picker screen when images is empty,
   *  renders commentSlot below the image in view mode, and hides the top Save
   *  button (sending is done from the comment input inside commentSlot). */
  mode?: 'edit' | 'comment'
  /** Called when user picks "Upload from gallery" on the source picker screen */
  onPickFromGallery?: () => void
  /** Called when the in-modal camera captures a photo. Receives an object-URL of the captured image. */
  onTakePhoto?: (url: string) => void
  /** Rendered below the image in view mode (use for comment input).
   *  Receives a function that returns the current pending edits so the
   *  caller can include them when submitting the comment. */
  commentSlot?: (getPendingEdits: () => Record<number, Blob>) => React.ReactNode
}

type ToolMode = 'draw' | 'text' | 'circle' | 'square' | 'arrow'
type EditorMode = 'view' | ToolMode

const TOOLBAR_H = 60
const FILMSTRIP_H = 88
const COMMENT_SLOT_H = 70

const COLOR_PALETTE: string[] = [
  '#FFFFFF',
  '#000000',
  '#FF3B30',
  '#FF9500',
  '#FFCC00',
  '#34C759',
  '#00C7BE',
  '#007AFF',
  '#5856D6',
  '#FF2D55',
  '#AF52DE',
]

/* ─── Inner component ─────────────────────────────────────────────────────── */
const ImageEditorInner: React.FC<ImageEditorModalProps> = ({
  images,
  startIndex = 0,
  trapFocus = false,
  onSave,
  onCancel,
  hasLocalImages = false,
  mode = 'edit',
  onPickFromGallery,
  onTakePhoto,
  commentSlot,
}) => {
  const canvasElRef = useRef<HTMLCanvasElement>(null)
  /** Fabric IText appends a hidden textarea here so modal overlays don't steal keyboard focus */
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const fabricRef = useRef<any>(null)
  const fabricModuleRef = useRef<any>(null)
  const autoAddTextRef = useRef(false)
  const autoAddShapeRef = useRef<string | null>(null)

  const pendingEditsRef = useRef<Record<number, Blob>>({})
  const [pendingEdits, setPendingEditsState] = useState<Record<number, Blob>>({})

  const thumbUrlsRef = useRef<Record<number, string>>({})
  const [thumbUrls, setThumbUrls] = useState<Record<number, string>>({})

  // ── Camera screen ────────────────────────────────────────────────────────
  const {
    isActive: showCamera,
    videoRef,
    open: openCamera,
    close: closeCamera,
    capture: handleCapture,
  } = useCameraCapture({ onCapture: (url) => onTakePhoto?.(url) })

  const [activeIndex, setActiveIndex] = useState(startIndex)
  const [editorMode, setEditorMode] = useState<EditorMode>('view')
  const [drawColor, setDrawColor] = useState('#FF0000')
  const drawColorRef = useRef('#FF0000')
  const [textColor, setTextColor] = useState('#FFFFFF')
  const textColorRef = useRef('#FFFFFF')
  const [bgColor, setBgColor] = useState('') // '' = no background
  const bgColorRef = useRef('')
  const [showTextColorPalette, setShowTextColorPalette] = useState(false)
  const [showBgColorPalette, setShowBgColorPalette] = useState(false)
  const [brushSize, setBrushSize] = useState(4)
  const [isSavingAll, setIsSavingAll] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)

  const t_comment = useLocaleText('comment_on_image')
  const t_save = useLocaleText('save')
  const t_done = useLocaleText('done')

  // editActive drives the Fabric init/cleanup — true for any edit tool
  const editActive = editorMode !== 'view'

  // Keep colour/bg refs always current to avoid stale closures in callbacks
  useEffect(() => {
    textColorRef.current = textColor
  }, [textColor])
  useEffect(() => {
    bgColorRef.current = bgColor
  }, [bgColor])

  const setPendingEdit = useCallback((index: number, blob: Blob) => {
    pendingEditsRef.current = { ...pendingEditsRef.current, [index]: blob }
    setPendingEditsState({ ...pendingEditsRef.current })

    if (thumbUrlsRef.current[index]) URL.revokeObjectURL(thumbUrlsRef.current[index])
    const thumbUrl = URL.createObjectURL(blob)
    thumbUrlsRef.current = { ...thumbUrlsRef.current, [index]: thumbUrl }
    setThumbUrls({ ...thumbUrlsRef.current })
  }, [])

  useEffect(() => {
    return () => {
      Object.values(thumbUrlsRef.current).forEach((u) => URL.revokeObjectURL(u))
    }
  }, [])

  /* ── Helper: focus Fabric's hidden IText textarea (must live inside the modal) ─ */
  const focusITextEditing = useCallback((itext: any) => {
    if (!itext) return
    const container = editorContainerRef.current
    if (container) itext.hiddenTextareaContainer = container
    // Defer until after toolbar button click finishes — otherwise focus stays on the button
    window.setTimeout(() => {
      if (!fabricRef.current || !itext.canvas) return
      fabricRef.current.setActiveObject(itext)
      itext.enterEditing()
      if (itext.hiddenTextarea) {
        itext.hiddenTextarea.style.zIndex = '100000'
        itext.hiddenTextarea.focus()
      }
      fabricRef.current.requestRenderAll()
    }, 0)
  }, [])

  /* ── Helper: add IText to live canvas ────────────────────────────────────── */
  const addITextToCanvas = useCallback(() => {
    const canvas = fabricRef.current
    const fabric = fabricModuleRef.current
    if (!canvas || !fabric) return
    const itext = new fabric.IText('Type here', {
      left: Math.round((canvas.width || 200) / 2),
      top: Math.round((canvas.height || 200) / 2),
      fontSize: 24,
      fill: textColorRef.current,
      backgroundColor: bgColorRef.current,
      fontFamily: 'Arial',
      originX: 'center',
      originY: 'center',
      hiddenTextareaContainer: editorContainerRef.current ?? undefined,
    })
    canvas.add(itext)
    focusITextEditing(itext)
  }, [focusITextEditing])

  /* ── Helper: add shapes to live canvas ───────────────────────────────────── */
  const addCircleToCanvas = useCallback(() => {
    const canvas = fabricRef.current
    const fabric = fabricModuleRef.current
    if (!canvas || !fabric) return
    const circle = new fabric.Circle({
      left: Math.round((canvas.width || 200) / 2),
      top: Math.round((canvas.height || 200) / 2),
      radius: 50,
      fill: 'transparent',
      stroke: drawColorRef.current,
      strokeWidth: 3,
      originX: 'center',
      originY: 'center',
    })
    canvas.add(circle)
    canvas.setActiveObject(circle)
    canvas.renderAll()
  }, [])

  const addRectToCanvas = useCallback(() => {
    const canvas = fabricRef.current
    const fabric = fabricModuleRef.current
    if (!canvas || !fabric) return
    const rect = new fabric.Rect({
      left: Math.round((canvas.width || 200) / 2),
      top: Math.round((canvas.height || 200) / 2),
      width: 100,
      height: 70,
      fill: 'transparent',
      stroke: drawColorRef.current,
      strokeWidth: 3,
      originX: 'center',
      originY: 'center',
    })
    canvas.add(rect)
    canvas.setActiveObject(rect)
    canvas.renderAll()
  }, [])

  const addArrowToCanvas = useCallback(() => {
    const canvas = fabricRef.current
    const fabric = fabricModuleRef.current
    if (!canvas || !fabric) return
    const cx = Math.round((canvas.width || 200) / 2)
    const cy = Math.round((canvas.height || 200) / 2)
    const color = drawColorRef.current
    const line = new fabric.Line([cx - 60, cy, cx + 50, cy], {
      stroke: color,
      strokeWidth: 3,
      selectable: false,
      evented: false,
    })
    const triangle = new fabric.Triangle({
      width: 16,
      height: 16,
      fill: color,
      left: cx + 58,
      top: cy,
      angle: 90,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    })
    const group = new fabric.Group([line, triangle])
    canvas.add(group)
    canvas.setActiveObject(group)
    canvas.renderAll()
  }, [])

  /* ── Fabric init — runs only when entering/exiting edit mode or changing image */
  /* ── Switching draw↔text does NOT re-run this effect                          */
  useEffect(() => {
    if (!editActive || !canvasElRef.current) return

    setIsImageLoading(true)
    setShowTextColorPalette(false)
    setShowBgColorPalette(false)

    let fabricCanvas: any = null
    let tempBlobUrl: string | null = null

    const timer = setTimeout(async () => {
      if (!canvasElRef.current) return

      const { fabric } = await import('fabric')
      fabricModuleRef.current = fabric

      const availW = window.innerWidth
      const availH = window.innerHeight - TOOLBAR_H

      fabricCanvas = new fabric.Canvas(canvasElRef.current, {
        isDrawingMode: editorMode === 'draw',
        width: availW,
        height: availH,
        backgroundColor: 'transparent',
      })

      fabricRef.current = fabricCanvas
      fabricCanvas.freeDrawingBrush.color = drawColorRef.current
      fabricCanvas.freeDrawingBrush.width = brushSize

      // Keep hidden textarea inside modal — body-level textarea loses keyboard focus in overlays
      fabricCanvas.on('text:editing:entered', (e: any) => {
        const target = e.target
        const container = editorContainerRef.current
        if (container) target.hiddenTextareaContainer = container
        if (target.hiddenTextarea) {
          target.hiddenTextarea.style.zIndex = '100000'
          target.hiddenTextarea.focus()
        }
      })

      // Sync text colour / bg controls when user selects a text object
      const syncFromTextObj = (obj: any) => {
        if (obj?.type === 'i-text' || obj?.type === 'text') {
          if (obj.fill && typeof obj.fill === 'string') setTextColor(obj.fill)
          setBgColor(obj.backgroundColor || '')
        }
      }
      fabricCanvas.on('selection:created', (e: any) => syncFromTextObj(e.selected?.[0]))
      fabricCanvas.on('selection:updated', (e: any) => syncFromTextObj(e.selected?.[0]))

      const pendingBlob = pendingEditsRef.current[activeIndex]
      let loadUrl: string
      if (pendingBlob) {
        tempBlobUrl = URL.createObjectURL(pendingBlob)
        loadUrl = tempBlobUrl
      } else {
        loadUrl = images[activeIndex] ?? ''
      }

      const isLocal = loadUrl.startsWith('blob:') || loadUrl.startsWith('data:')

      fabric.Image.fromURL(
        loadUrl,
        (img: any) => {
          if (!img || !fabricCanvas) {
            setIsImageLoading(false)
            return
          }

          const iw = img.width || availW
          const ih = img.height || availH
          const scale = Math.min(availW / iw, availH / ih, 1)

          fabricCanvas.setWidth(Math.round(iw * scale))
          fabricCanvas.setHeight(Math.round(ih * scale))

          img.set({ left: 0, top: 0, scaleX: scale, scaleY: scale, selectable: false, evented: false })
          fabricCanvas.add(img)
          fabricCanvas.sendToBack(img)
          fabricCanvas.renderAll()
          setIsImageLoading(false)

          // Auto-add text if entering text mode from view mode
          if (autoAddTextRef.current) {
            autoAddTextRef.current = false
            addITextToCanvas()
          }
          // Auto-add shape if entering shape mode from view mode
          if (autoAddShapeRef.current) {
            const shapeType = autoAddShapeRef.current
            autoAddShapeRef.current = null
            if (shapeType === 'circle') addCircleToCanvas()
            else if (shapeType === 'square') addRectToCanvas()
            else if (shapeType === 'arrow') addArrowToCanvas()
          }
        },
        isLocal ? {} : { crossOrigin: 'anonymous' }
      )
    }, 60)

    return () => {
      clearTimeout(timer)
      if (fabricCanvas) fabricCanvas.dispose()
      fabricRef.current = null
      if (tempBlobUrl) URL.revokeObjectURL(tempBlobUrl)
    }
    // editActive covers view↔edit transitions; activeIndex covers image changes.
    // mode is intentionally excluded: draw↔text switches must NOT recreate the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, editActive])

  /* ── Sync tool + brush to live canvas (draw↔text switches, colour, size) ─── */
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas || !editActive) return

    canvas.isDrawingMode = editorMode === 'draw'
    canvas.selection = editorMode !== 'draw'
    if (canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = drawColorRef.current
      canvas.freeDrawingBrush.width = brushSize
    }

    // When switching TO text mode while canvas is already alive, add a text object
    if (editorMode === 'text' && autoAddTextRef.current && fabricModuleRef.current) {
      autoAddTextRef.current = false
      addITextToCanvas()
    }
    // When switching TO a shape mode while canvas is already alive, add the shape
    if (
      (editorMode === 'circle' || editorMode === 'square' || editorMode === 'arrow') &&
      autoAddShapeRef.current &&
      fabricModuleRef.current
    ) {
      const shapeType = autoAddShapeRef.current
      autoAddShapeRef.current = null
      if (shapeType === 'circle') addCircleToCanvas()
      else if (shapeType === 'square') addRectToCanvas()
      else if (shapeType === 'arrow') addArrowToCanvas()
    }
  }, [
    editorMode,
    drawColor,
    brushSize,
    editActive,
    addITextToCanvas,
    addCircleToCanvas,
    addRectToCanvas,
    addArrowToCanvas,
  ])

  /* ── Apply bgColor change to currently selected text object ─────────────── */
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return
    const active = canvas.getActiveObject()
    if (active && (active.type === 'i-text' || active.type === 'text')) {
      active.set('backgroundColor', bgColor)
      canvas.renderAll()
    }
  }, [bgColor])

  /* ── Backspace / Delete: remove selected object or undo last stroke ───────── */
  useEffect(() => {
    if (!editActive) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return
      const canvas = fabricRef.current
      if (!canvas) return
      const active = canvas.getActiveObject()
      // If IText is in editing mode, let Fabric handle character deletion
      if (active && (active as any).isEditing) return
      e.preventDefault()
      if (active) {
        canvas.remove(active)
        canvas.discardActiveObject()
      } else {
        // Nothing selected — undo last annotation
        const objects = canvas.getObjects()
        if (objects.length > 1) canvas.remove(objects[objects.length - 1])
      }
      canvas.renderAll()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editActive])

  /* ── Apply textColor change to currently selected text object ────────────── */
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return
    const active = canvas.getActiveObject()
    if (active && (active.type === 'i-text' || active.type === 'text')) {
      active.set('fill', textColor)
      canvas.renderAll()
    }
  }, [textColor])

  /* ── Colour handlers ─────────────────────────────────────────────────────── */
  const handleDrawColorChange = (newColor: string) => {
    drawColorRef.current = newColor
    setDrawColor(newColor)
    const canvas = fabricRef.current
    if (canvas?.freeDrawingBrush) canvas.freeDrawingBrush.color = newColor
  }

  const handleTextColorSelect = (color: string) => {
    setTextColor(color)
    setShowTextColorPalette(false)
  }

  const handleBgColorSelect = (color: string) => {
    setBgColor(color)
    setShowBgColorPalette(false)
  }

  /* ── Enter edit mode tools ───────────────────────────────────────────────── */
  const enterDraw = () => {
    autoAddTextRef.current = false
    setEditorMode('draw')
  }

  const enterText = () => {
    if (editorMode === 'text') {
      addITextToCanvas()
      return
    }
    autoAddTextRef.current = true
    setEditorMode('text')
  }

  const enterCircle = () => {
    if (editorMode === 'circle') {
      addCircleToCanvas()
      return
    }
    autoAddTextRef.current = false
    autoAddShapeRef.current = 'circle'
    setEditorMode('circle')
  }

  const enterSquare = () => {
    if (editorMode === 'square') {
      addRectToCanvas()
      return
    }
    autoAddTextRef.current = false
    autoAddShapeRef.current = 'square'
    setEditorMode('square')
  }

  const enterArrow = () => {
    if (editorMode === 'arrow') {
      addArrowToCanvas()
      return
    }
    autoAddTextRef.current = false
    autoAddShapeRef.current = 'arrow'
    setEditorMode('arrow')
  }

  /* ── Exit edit mode without saving ──────────────────────────────────────── */
  const handleCancelEdit = () => {
    autoAddShapeRef.current = null
    setShowTextColorPalette(false)
    setShowBgColorPalette(false)
    setEditorMode('view')
  }

  /* ── Fabric actions ──────────────────────────────────────────────────────── */
  const handleUndo = () => {
    const canvas = fabricRef.current
    if (!canvas) return
    const objects = canvas.getObjects()
    if (objects.length > 1) {
      canvas.remove(objects[objects.length - 1])
      canvas.renderAll()
    }
  }

  const handleClear = () => {
    const canvas = fabricRef.current
    if (!canvas) return
    canvas
      .getObjects()
      .slice(1)
      .forEach((obj: any) => canvas.remove(obj))
    canvas.renderAll()
  }

  /* ── "Done": save canvas locally → back to View Mode ────────────────────── */
  const handleDone = () => {
    setShowTextColorPalette(false)
    setShowBgColorPalette(false)
    const canvas = fabricRef.current
    if (!canvas) {
      setEditorMode('view')
      return
    }
    const dataUrl = canvas.toDataURL({ format: 'png', quality: 1 })
    fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => {
        setPendingEdit(activeIndex, blob)
        setEditorMode('view')
      })
      .catch(() => setEditorMode('view'))
  }

  /* ── Navigation (View Mode only) ────────────────────────────────────────── */
  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1))
  const goNext = () => setActiveIndex((i) => Math.min(images.length - 1, i + 1))

  /* ── Save all ────────────────────────────────────────────────────────────── */
  const handleSaveAll = () => {
    setIsSavingAll(true)
    onSave(pendingEditsRef.current)
  }

  const hasPendingEdits = Object.keys(pendingEdits).length > 0
  const isEditing = editorMode !== 'view'
  const isShapeMode = editorMode === 'circle' || editorMode === 'square' || editorMode === 'arrow'

  // Exposed to commentSlot so caller can read current edits at submit time
  const getPendingEdits = useCallback(() => pendingEditsRef.current, [])

  const viewSrc = thumbUrls[activeIndex] ?? images[activeIndex] ?? ''
  const viewSrcIsLocal = viewSrc.startsWith('blob:') || viewSrc.startsWith('data:')

  /* ── Reusable toolbar components ─────────────────────────────────────────── */
  const ToolBtn: React.FC<{
    active?: boolean
    danger?: boolean
    label: string
    onClick: () => void
    children: React.ReactNode
  }> = ({ active, danger, label, onClick, children }) => (
    <PseudoBox
      as="button"
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="36px"
      h="36px"
      borderRadius="50%"
      border="none"
      bg={danger ? 'rgba(239,68,68,0.2)' : active ? 'rgba(255,255,255,0.26)' : 'rgba(255,255,255,0.1)'}
      color={danger ? '#FCA5A5' : active ? '#ffffff' : '#D1D5DB'}
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: danger ? 'rgba(239,68,68,0.32)' : 'rgba(255,255,255,0.22)' }}
      _focus={{ outline: 'none' }}
      title={label}
      style={{
        padding: 0,
        flexShrink: 0,
        boxShadow: active ? '0 0 0 1.5px rgba(255,255,255,0.5)' : 'none',
      }}
    >
      {children}
    </PseudoBox>
  )

  const Divider = () => <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

  const isCommentMode = mode === 'comment'
  const showSourcePicker = isCommentMode && images.length === 0

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div
      ref={editorContainerRef}
      role={trapFocus ? 'dialog' : undefined}
      aria-modal={trapFocus ? true : undefined}
      aria-label={trapFocus ? t_comment : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0,0,0,0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={() => {
        setShowTextColorPalette(false)
        setShowBgColorPalette(false)
      }}
    >
      {/* ── SOURCE PICKER (comment mode, no images yet) ───────────────────── */}
      {showSourcePicker && (
        <>
          {/* Close button */}
          <div
            style={{ position: 'absolute', top: 14, left: 14, zIndex: 3 }}
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
          >
            <PseudoBox
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="36px"
              h="36px"
              borderRadius="50%"
              border="none"
              bg="rgba(255,255,255,0.1)"
              color="#D1D5DB"
              cursor="pointer"
              _hover={{ bg: 'rgba(255,255,255,0.2)', color: '#fff' }}
              _focus={{ outline: 'none' }}
              style={{ padding: 0 }}
            >
              <X size={17} />
            </PseudoBox>
          </div>

          <Flex flexDirection="column" alignItems="center" style={{ gap: 20 }} onClick={(e) => e.stopPropagation()}>
            <Text color="#F3F4F6" fontSize="16px" fontWeight="600" mb="8px">
              Add photo
            </Text>

            <Flex flexDirection="row" style={{ gap: 16 }}>
              {/* Take a photo */}
              {onTakePhoto && (
                <PseudoBox
                  as="button"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  w="130px"
                  h="120px"
                  borderRadius="16px"
                  border="2px solid rgba(255,255,255,0.35)"
                  bg="rgba(255,255,255,0.18)"
                  color="white"
                  cursor="pointer"
                  _hover={{ bg: 'rgba(255,255,255,0.28)', borderColor: 'rgba(255,255,255,0.6)' }}
                  _focus={{ outline: 'none' }}
                  onClick={openCamera}
                  style={{ gap: 10 }}
                >
                  <Camera size={36} color="white" />
                  <Text fontSize="13px" color="white" fontWeight="600" mt="6px">
                    Take photo
                  </Text>
                </PseudoBox>
              )}

              {/* Upload from gallery */}
              {onPickFromGallery && (
                <PseudoBox
                  as="button"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  w="130px"
                  h="120px"
                  borderRadius="16px"
                  border="2px solid rgba(255,255,255,0.35)"
                  bg="rgba(255,255,255,0.18)"
                  color="white"
                  cursor="pointer"
                  _hover={{ bg: 'rgba(255,255,255,0.28)', borderColor: 'rgba(255,255,255,0.6)' }}
                  _focus={{ outline: 'none' }}
                  onClick={onPickFromGallery}
                  style={{ gap: 10 }}
                >
                  <ImagePlus size={36} color="white" />
                  <Text fontSize="13px" color="white" fontWeight="600" mt="6px">
                    Gallery
                  </Text>
                </PseudoBox>
              )}
            </Flex>
          </Flex>
        </>
      )}

      {/* ── CAMERA SCREEN ─────────────────────────────────────────────────── */}
      {showCamera && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            background: '#000',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Back */}
          <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 6 }} onClick={closeCamera}>
            <PseudoBox
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="36px"
              h="36px"
              borderRadius="50%"
              border="none"
              bg="rgba(255,255,255,0.1)"
              color="#D1D5DB"
              cursor="pointer"
              _hover={{ bg: 'rgba(255,255,255,0.2)', color: '#fff' }}
              _focus={{ outline: 'none' }}
              style={{ padding: 0 }}
            >
              <X size={17} />
            </PseudoBox>
          </div>

          {/* Video stream */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 120px)',
              borderRadius: 12,
              objectFit: 'cover',
            }}
          />

          {/* Capture button */}
          <div style={{ marginTop: 24 }}>
            <PseudoBox
              as="button"
              w="68px"
              h="68px"
              borderRadius="50%"
              border="4px solid white"
              bg="rgba(255,255,255,0.15)"
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ bg: 'rgba(255,255,255,0.3)' }}
              _focus={{ outline: 'none' }}
              onClick={handleCapture}
              style={{ padding: 0 }}
            >
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'white',
                }}
              />
            </PseudoBox>
          </div>
        </div>
      )}

      {/* ── EDITOR / VIEWER (images present) ─────────────────────────────── */}
      {!showSourcePicker && !showCamera && (
        <>
          {/* ── TOOLBAR ──────────────────────────────────────────────────────── */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: TOOLBAR_H,
              display: 'flex',
              alignItems: 'center',
              padding: '0 14px',
              gap: 8,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
              zIndex: 2,
            }}
          >
            {isEditing ? (
              /* ── EDIT MODE toolbar ───────────────────────────────────────── */
              <>
                {/* X — exit edit mode without saving */}
                <ToolBtn label="Cancel edit" onClick={handleCancelEdit}>
                  <X size={17} />
                </ToolBtn>

                <div style={{ flex: 1 }} />

                {/* Tool selectors — always visible in edit mode */}
                <ToolBtn active={editorMode === 'draw'} label="Draw tool" onClick={enterDraw}>
                  <Pencil size={15} />
                </ToolBtn>
                <ToolBtn active={editorMode === 'text'} label="Add text" onClick={enterText}>
                  <Type size={15} />
                </ToolBtn>
                <ToolBtn active={editorMode === 'circle'} label="Add circle" onClick={enterCircle}>
                  <Circle size={15} />
                </ToolBtn>
                <ToolBtn active={editorMode === 'square'} label="Add square" onClick={enterSquare}>
                  <Square size={15} />
                </ToolBtn>
                <ToolBtn active={editorMode === 'arrow'} label="Add arrow" onClick={enterArrow}>
                  <ArrowRight size={15} />
                </ToolBtn>

                <Divider />

                {/* Draw tool context controls */}
                {editorMode === 'draw' && (
                  <Flex alignItems="center" style={{ gap: 8, flexShrink: 0 }}>
                    {/* Colour swatch with native colour picker overlay */}
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          backgroundColor: drawColor,
                          border: '2px solid rgba(255,255,255,0.55)',
                          cursor: 'pointer',
                        }}
                      />
                      <input
                        type="color"
                        value={drawColor}
                        onChange={(e) => handleDrawColorChange(e.target.value)}
                        title="Draw colour"
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          left: 0,
                          top: 0,
                        }}
                      />
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={brushSize}
                      onChange={(e) => setBrushSize(Number(e.target.value))}
                      style={{ width: 72, accentColor: '#60A5FA' } as React.CSSProperties}
                    />
                  </Flex>
                )}

                {/* Shape tool context controls — colour picker only */}
                {isShapeMode && (
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        backgroundColor: drawColor,
                        border: '2px solid rgba(255,255,255,0.55)',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="color"
                      value={drawColor}
                      onChange={(e) => handleDrawColorChange(e.target.value)}
                      title="Shape colour"
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        left: 0,
                        top: 0,
                      }}
                    />
                  </div>
                )}

                {/* Text tool context controls */}
                {editorMode === 'text' && (
                  <Flex alignItems="center" style={{ gap: 6, flexShrink: 0, position: 'relative' }}>
                    {/* ── "A" button: white letter + colored underline bar → text color palette ── */}
                    <div style={{ position: 'relative' }}>
                      <PseudoBox
                        as="button"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        w="34px"
                        h="36px"
                        borderRadius="6px"
                        border="none"
                        bg={showTextColorPalette ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'}
                        cursor="pointer"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          setShowTextColorPalette((v) => !v)
                          setShowBgColorPalette(false)
                        }}
                        _hover={{ bg: 'rgba(255,255,255,0.18)' }}
                        _focus={{ outline: 'none' }}
                        title="Text colour"
                        style={{ padding: '4px 6px 5px', gap: 2, flexShrink: 0 }}
                      >
                        <span
                          style={{ fontSize: 15, fontWeight: 800, color: '#fff', lineHeight: 1, fontFamily: 'Arial' }}
                        >
                          A
                        </span>
                        {/* Colour underline bar */}
                        <div style={{ width: '100%', height: 3, borderRadius: 2, background: textColor }} />
                      </PseudoBox>

                      {/* Text colour palette dropdown */}
                      {showTextColorPalette && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: 42,
                            left: 0,
                            display: 'flex',
                            gap: 8,
                            padding: '10px 12px',
                            background: 'rgba(28,28,28,0.97)',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.12)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                            zIndex: 10,
                            flexWrap: 'wrap',
                            width: 196,
                          }}
                        >
                          {COLOR_PALETTE.map((color) => (
                            <div
                              key={color}
                              onClick={() => handleTextColorSelect(color)}
                              title={color}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: color,
                                cursor: 'pointer',
                                border:
                                  color === textColor
                                    ? '2.5px solid #60A5FA'
                                    : color === '#FFFFFF'
                                    ? '1.5px solid rgba(255,255,255,0.3)'
                                    : '1.5px solid rgba(255,255,255,0.1)',
                                boxShadow: color === textColor ? '0 0 0 1px #60A5FA' : 'none',
                                flexShrink: 0,
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ── Background color square → background color palette ── */}
                    <div style={{ position: 'relative' }}>
                      <PseudoBox
                        as="button"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="34px"
                        h="36px"
                        borderRadius="6px"
                        border="none"
                        bg={showBgColorPalette ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'}
                        cursor="pointer"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          setShowBgColorPalette((v) => !v)
                          setShowTextColorPalette(false)
                        }}
                        _hover={{ bg: 'rgba(255,255,255,0.18)' }}
                        _focus={{ outline: 'none' }}
                        title="Text background colour"
                        style={{ padding: '6px', flexShrink: 0 }}
                      >
                        {bgColor ? (
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              backgroundColor: bgColor,
                              border: '1.5px solid rgba(255,255,255,0.5)',
                            }}
                          />
                        ) : (
                          /* No background — strikethrough square */
                          <div style={{ position: 'relative', width: 20, height: 20, flexShrink: 0 }}>
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                border: '1.5px dashed rgba(255,255,255,0.4)',
                              }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 1.5,
                                background: '#FF3B30',
                                transform: 'translateY(-50%) rotate(-45deg)',
                              }}
                            />
                          </div>
                        )}
                      </PseudoBox>

                      {/* Background colour palette dropdown */}
                      {showBgColorPalette && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: 42,
                            left: 0,
                            display: 'flex',
                            gap: 8,
                            padding: '10px 12px',
                            background: 'rgba(28,28,28,0.97)',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.12)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                            zIndex: 10,
                            flexWrap: 'wrap',
                            width: 196,
                          }}
                        >
                          {/* "No background" option */}
                          <div
                            onClick={() => handleBgColorSelect('')}
                            title="No background"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              border: !bgColor ? '2.5px solid #60A5FA' : '1.5px dashed rgba(255,255,255,0.4)',
                              boxShadow: !bgColor ? '0 0 0 1px #60A5FA' : 'none',
                              position: 'relative',
                              overflow: 'hidden',
                              flexShrink: 0,
                              background: 'transparent',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 2,
                                background: '#FF3B30',
                                transform: 'translateY(-50%) rotate(-45deg)',
                              }}
                            />
                          </div>
                          {COLOR_PALETTE.map((color) => (
                            <div
                              key={color}
                              onClick={() => handleBgColorSelect(color)}
                              title={color}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: color,
                                cursor: 'pointer',
                                border:
                                  color === bgColor
                                    ? '2.5px solid #60A5FA'
                                    : color === '#FFFFFF'
                                    ? '1.5px solid rgba(255,255,255,0.3)'
                                    : '1.5px solid rgba(255,255,255,0.1)',
                                boxShadow: color === bgColor ? '0 0 0 1px #60A5FA' : 'none',
                                flexShrink: 0,
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </Flex>
                )}

                <Divider />

                <ToolBtn label="Undo last" onClick={handleUndo}>
                  <RotateCcw size={15} />
                </ToolBtn>
                <ToolBtn danger label="Clear all" onClick={handleClear}>
                  <Trash2 size={15} />
                </ToolBtn>

                <Divider />

                {/* Done — save canvas locally, back to View Mode */}
                <Button
                  variantColor="green"
                  size="sm"
                  onClick={handleDone}
                  style={{ flexShrink: 0, fontSize: 13, fontWeight: 600 }}
                >
                  {t_done}
                </Button>
              </>
            ) : (
              /* ── VIEW MODE toolbar ───────────────────────────────────────── */
              <>
                {/* X — close modal entirely */}
                <PseudoBox
                  as="button"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="36px"
                  h="36px"
                  borderRadius="50%"
                  border="none"
                  bg="rgba(255,255,255,0.1)"
                  color="#D1D5DB"
                  cursor="pointer"
                  onClick={onCancel}
                  _hover={{ bg: 'rgba(255,255,255,0.2)', color: '#fff' }}
                  _focus={{ outline: 'none' }}
                  title="Close"
                  style={{ padding: 0, flexShrink: 0 }}
                >
                  <X size={17} />
                </PseudoBox>

                <div style={{ flex: 1 }} />

                {/* Dot indicators */}
                {images.length > 1 && (
                  <Flex alignItems="center" style={{ gap: 5, flexShrink: 0 }}>
                    {images.map((_, i) => (
                      <div
                        key={i}
                        onClick={() => setActiveIndex(i)}
                        style={{
                          width: i === activeIndex ? 8 : 6,
                          height: i === activeIndex ? 8 : 6,
                          borderRadius: '50%',
                          backgroundColor: pendingEdits[i]
                            ? '#34D399'
                            : i === activeIndex
                            ? '#F3F4F6'
                            : 'rgba(255,255,255,0.35)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </Flex>
                )}

                <Divider />

                <Button
                  variantColor="blue"
                  size="sm"
                  onClick={handleSaveAll}
                  isLoading={isSavingAll}
                  loadingText="Uploading…"
                  isDisabled={!hasPendingEdits && !hasLocalImages}
                  style={{ flexShrink: 0, fontSize: 12 }}
                >
                  {/* Save{hasPendingEdits ? ` (${Object.keys(pendingEdits).length})` : ''} */}
                  {t_save}
                </Button>

                <Divider />

                {/* Entry points to edit mode */}
                <ToolBtn label="Draw on image" onClick={enterDraw}>
                  <Pencil size={16} />
                </ToolBtn>
                <ToolBtn label="Add text" onClick={enterText}>
                  <Type size={16} />
                </ToolBtn>
                <ToolBtn label="Add circle" onClick={enterCircle}>
                  <Circle size={16} />
                </ToolBtn>
                <ToolBtn label="Add square" onClick={enterSquare}>
                  <Square size={16} />
                </ToolBtn>
                <ToolBtn label="Add arrow" onClick={enterArrow}>
                  <ArrowRight size={16} />
                </ToolBtn>
              </>
            )}
          </div>

          {/* ── VIEW MODE: plain image ────────────────────────────────────────── */}
          {!isEditing && (
            <div
              style={{
                maxWidth: '100vw',
                maxHeight: `calc(100vh - ${TOOLBAR_H}px - ${images.length > 1 ? FILMSTRIP_H : 0}px - ${
                  commentSlot ? COMMENT_SLOT_H : 0
                }px)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 60px',
              }}
            >
              <img
                src={viewSrc}
                alt={`Image ${activeIndex + 1}`}
                crossOrigin={viewSrcIsLocal ? undefined : 'anonymous'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4, display: 'block' }}
              />
            </div>
          )}

          {/* ── COMMENT SLOT — View Mode only ─────────────────────────────────── */}
          {!isEditing && commentSlot && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: images.length > 1 ? FILMSTRIP_H : 0,
                left: 0,
                right: 0,
                minHeight: COMMENT_SLOT_H,
                padding: '8px 12px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
                zIndex: 3,
              }}
            >
              {commentSlot(getPendingEdits)}
            </div>
          )}

          {/* ── EDIT MODE: Fabric canvas ──────────────────────────────────────── */}
          {isEditing && (
            <>
              {isImageLoading && (
                <Text color="#6B7280" fontSize="14px" style={{ position: 'absolute', zIndex: 1 }}>
                  Loading…
                </Text>
              )}
              <div
                style={{
                  opacity: isImageLoading ? 0 : 1,
                  transition: 'opacity 0.2s',
                  lineHeight: 0,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <canvas ref={canvasElRef} style={{ borderRadius: 4 }} />
              </div>
            </>
          )}

          {/* ── PREV / NEXT arrows — View Mode only ──────────────────────────── */}
          {!isEditing && activeIndex > 0 && <NavArrow side="left" onClick={goPrev} />}
          {!isEditing && activeIndex < images.length - 1 && <NavArrow side="right" onClick={goNext} />}

          {/* ── FILMSTRIP — View Mode only ────────────────────────────────────── */}
          {!isEditing && images.length > 1 && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: FILMSTRIP_H,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 16px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                overflowX: 'auto',
                zIndex: 2,
              }}
            >
              {images.map((url, i) => {
                const isActive = i === activeIndex
                const isEdited = !!pendingEdits[i]
                const src = (thumbUrls[i] ?? url).split('?')[0]
                const srcIsLocal = src.startsWith('blob:') || src.startsWith('data:')
                return (
                  <div
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    title={isEdited ? `Image ${i + 1} (edited)` : `Image ${i + 1}`}
                    style={{
                      flexShrink: 0,
                      width: isActive ? 64 : 52,
                      height: isActive ? 64 : 52,
                      borderRadius: 6,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: isActive
                        ? '2.5px solid #F3F4F6'
                        : isEdited
                        ? '2.5px solid #34D399'
                        : '2px solid rgba(255,255,255,0.25)',
                      boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3), 0 4px 12px rgba(0,0,0,0.5)' : 'none',
                      opacity: isActive ? 1 : 0.72,
                      transition: 'all 0.15s',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={src}
                      alt={`thumb-${i}`}
                      crossOrigin={srcIsLocal ? undefined : 'anonymous'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {isEdited && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 3,
                          right: 3,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#34D399',
                          border: '1.5px solid rgba(0,0,0,0.4)',
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {/* ── end !showSourcePicker ─────────────────────────────────────────── */}
        </>
      )}
    </div>
  )
}

/* ─── Navigation arrow ───────────────────────────────────────────────────── */
const NavArrow: React.FC<{ side: 'left' | 'right'; onClick: () => void }> = ({ side, onClick }) => (
  <button
    onClick={onClick}
    style={{
      position: 'absolute',
      [side]: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 3,
      background: 'rgba(0,0,0,0.5)',
      border: 'none',
      borderRadius: 8,
      width: 44,
      height: 44,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#F3F4F6',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.75)')}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.5)')}
    title={side === 'left' ? 'Previous' : 'Next'}
  >
    {side === 'left' ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
  </button>
)

/* ─── Public component — portal + guard ──────────────────────────────────── */
export const ImageEditorModal: React.FC<ImageEditorModalProps> = (props) => {
  const isCommentMode = props.mode === 'comment'
  // In comment mode allow opening with empty images (shows source picker)
  if (!props.isOpen) return null
  if (!isCommentMode && props.images.length === 0) return null
  return ReactDOM.createPortal(
    props.trapFocus ? (
      <FocusLock returnFocus>
        <ImageEditorInner {...props} />
      </FocusLock>
    ) : (
      <ImageEditorInner {...props} />
    ),
    document.body
  )
}
